import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

/* ═══════════════════════════════════════════════════════════════════════
 * EngagementSurvey — public, no login required.
 * Anonymous bi-monthly Gallup-style survey. Soft-locks by fingerprint so
 * a browser can only submit once per period. Optional demographics.
 * ═══════════════════════════════════════════════════════════════════════ */

const SCALE_LABELS = {
    1: "Strongly Disagree",
    2: "Disagree",
    3: "Neutral",
    4: "Agree",
    5: "Strongly Agree",
};

const SCALE_COLORS = {
    1: "#DC2626",   // red
    2: "#F97316",   // orange
    3: "#94A3B8",   // slate
    4: "#22C55E",   // green
    5: "#065F46",   // dark green
};

// Simple browser fingerprint — enough to soft-block a resubmit from the
// same device without touching cookies. Never sent for tracking.
function makeFingerprint() {
    try {
        const s = [
            navigator.userAgent, navigator.language,
            navigator.platform, `${screen.width}x${screen.height}`,
            new Date().getTimezoneOffset(),
        ].join("|");
        let h = 5381;
        for (let i = 0; i < s.length; i++) h = ((h << 5) + h) + s.charCodeAt(i);
        return `fp_${(h >>> 0).toString(16)}`;
    } catch {
        return `fp_anon_${Math.random().toString(16).slice(2)}`;
    }
}

export default function EngagementSurvey() {
    const [config, setConfig] = useState(null);
    const [answers, setAnswers] = useState({});
    const [department, setDepartment] = useState("");
    const [tenure, setTenure] = useState("");
    const [terminal, setTerminal] = useState("");
    const [comment, setComment] = useState("");
    const [fp] = useState(makeFingerprint());
    const [alreadyDone, setAlreadyDone] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/engagement/config").then((r) => setConfig(r.data))
            .catch(() => setConfig(null));
        api.get(`/engagement/check?fingerprint=${encodeURIComponent(fp)}`)
            .then((r) => setAlreadyDone(!!r.data?.already_submitted))
            .catch(() => setAlreadyDone(false));
    }, [fp]);

    const bySection = useMemo(() => {
        if (!config?.questions) return [];
        const map = {};
        for (const q of config.questions) {
            (map[q.section] ||= { section: q.section, label: q.section_label, questions: [] })
                .questions.push(q);
        }
        return Object.values(map);
    }, [config]);

    const remaining = useMemo(() => {
        if (!config?.questions) return 0;
        return config.questions.filter((q) => !answers[q.key]).length;
    }, [answers, config]);

    const overallDone = config?.questions?.length
        ? config.questions.length - remaining
        : 0;
    const overallTotal = config?.questions?.length || 28;

    const setAns = (key, value) =>
        setAnswers((a) => ({ ...a, [key]: value }));

    const submit = async () => {
        if (!config) return;
        if (remaining > 0) {
            toast.error(`Please answer all questions — ${remaining} remaining.`);
            const missing = config.questions.find((q) => !answers[q.key]);
            if (missing) {
                document.getElementById(`q-row-${missing.key}`)
                    ?.scrollIntoView({ behavior: "smooth", block: "center" });
            }
            return;
        }
        setBusy(true);
        try {
            await api.post("/engagement/submit", {
                answers,
                department: department || undefined,
                tenure_band: tenure || undefined,
                terminal: terminal || undefined,
                comment: comment.trim() || undefined,
                fingerprint: fp,
            });
            setSubmitted(true);
            toast.success("Thank you — your response has been submitted anonymously.");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setBusy(false); }
    };

    if (!config) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-600">
                Loading survey…
            </div>
        );
    }

    if (submitted) {
        return (
            <SurveyShell config={config}>
                <div className="ttv-survey-thanks" data-testid="engagement-thanks">
                    <div className="text-6xl mb-4">✓</div>
                    <h2 className="text-2xl font-black mb-2">Thank you!</h2>
                    <p className="text-slate-600 max-w-md mx-auto">
                        Your response has been submitted <b>anonymously</b>.
                        The results are aggregated to help HR act on what matters.
                        The next survey window opens on the next bi-monthly cycle.
                    </p>
                    <Link to="/login" data-testid="back-to-login"
                          className="mt-8 inline-block px-6 py-3 rounded-full bg-emerald-600 text-white font-bold hover:bg-emerald-700 transition">
                        Back to sign-in
                    </Link>
                </div>
            </SurveyShell>
        );
    }

    if (alreadyDone) {
        return (
            <SurveyShell config={config}>
                <div className="ttv-survey-thanks" data-testid="engagement-already">
                    <div className="text-6xl mb-4">🔒</div>
                    <h2 className="text-2xl font-black mb-2">You&apos;ve already responded</h2>
                    <p className="text-slate-600 max-w-md mx-auto">
                        This device has already submitted a response for {config.current_period_human}.
                        Each device can only submit once per bi-monthly period to keep the data honest.
                    </p>
                    <Link to="/login" data-testid="back-to-login"
                          className="mt-8 inline-block px-6 py-3 rounded-full bg-slate-700 text-white font-bold hover:bg-slate-800 transition">
                        Back to sign-in
                    </Link>
                </div>
            </SurveyShell>
        );
    }

    return (
        <SurveyShell config={config}>
            {/* Progress bar */}
            <div className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-slate-200 -mx-4 px-4 py-2 md:py-3 mb-4">
                <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-bold text-slate-700">
                        {overallDone}/{overallTotal} answered
                    </span>
                    <span className="text-slate-500">
                        {remaining === 0 ? "All set — you can submit" : `${remaining} to go`}
                    </span>
                </div>
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 transition-all"
                         style={{ width: `${(overallDone / overallTotal) * 100}%` }} />
                </div>
            </div>

            {/* Optional demographics */}
            <div className="mb-6 p-4 rounded-xl bg-slate-50 border border-slate-200">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">
                    Optional · Helps HR slice results — never used to identify you
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <label className="block">
                        <span className="text-xs font-semibold text-slate-700">Department</span>
                        <select value={department} onChange={(e) => setDepartment(e.target.value)}
                                data-testid="survey-department"
                                className="ttv-input mt-1">
                            <option value="">— Prefer not to say —</option>
                            {config.departments.map((d) => <option key={d} value={d}>{d}</option>)}
                        </select>
                    </label>
                    <label className="block">
                        <span className="text-xs font-semibold text-slate-700">Time with company</span>
                        <select value={tenure} onChange={(e) => setTenure(e.target.value)}
                                data-testid="survey-tenure"
                                className="ttv-input mt-1">
                            <option value="">— Prefer not to say —</option>
                            {config.tenure_bands.map((d) => <option key={d} value={d}>{d}</option>)}
                        </select>
                    </label>
                    <label className="block">
                        <span className="text-xs font-semibold text-slate-700">Terminal</span>
                        <select value={terminal} onChange={(e) => setTerminal(e.target.value)}
                                data-testid="survey-terminal"
                                className="ttv-input mt-1">
                            <option value="">— Prefer not to say —</option>
                            {config.terminals.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                        </select>
                    </label>
                </div>
            </div>

            {/* Questions grouped by section */}
            {bySection.map((sec) => (
                <div key={sec.section} className="mb-6" data-testid={`survey-section-${sec.section}`}>
                    <div className="flex items-baseline gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-emerald-600 text-white grid place-items-center font-black">
                            {sec.section}
                        </div>
                        <h3 className="text-lg md:text-xl font-black text-slate-900">{sec.label}</h3>
                    </div>
                    <div className="space-y-3">
                        {sec.questions.map((q, idx) => (
                            <div id={`q-row-${q.key}`} key={q.key}
                                 data-testid={`survey-q-${q.key}`}
                                 className="rounded-xl border border-slate-200 bg-white p-4 hover:border-emerald-300 transition">
                                <div className="text-sm md:text-base text-slate-800 mb-3">
                                    <span className="text-slate-400 font-bold mr-2">{idx + 1}.</span>
                                    {q.text}
                                </div>
                                <div className="grid grid-cols-5 gap-1.5 md:gap-2">
                                    {[1,2,3,4,5].map((v) => {
                                        const selected = answers[q.key] === v;
                                        return (
                                            <button key={v} type="button"
                                                    data-testid={`survey-${q.key}-${v}`}
                                                    onClick={() => setAns(q.key, v)}
                                                    className={`px-1.5 py-2.5 rounded-lg border-2 transition-all text-[10px] md:text-xs font-bold uppercase tracking-wide
                                                                ${selected
                                                                  ? "text-white shadow-md scale-105"
                                                                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
                                                    style={selected ? {
                                                        backgroundColor: SCALE_COLORS[v],
                                                        borderColor: SCALE_COLORS[v],
                                                    } : undefined}>
                                                <div className="text-base md:text-lg font-black">{v}</div>
                                                <div className="hidden md:block leading-tight mt-0.5">
                                                    {SCALE_LABELS[v]}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}

            {/* Optional free-text */}
            <div className="mb-6">
                <label className="block">
                    <span className="text-sm font-bold text-slate-700">
                        Anything else you&apos;d like HR to know? <span className="text-slate-400 font-normal">(optional)</span>
                    </span>
                    <textarea rows={4} value={comment}
                              onChange={(e) => setComment(e.target.value)}
                              maxLength={2000}
                              placeholder="Your comment stays anonymous."
                              data-testid="survey-comment"
                              className="ttv-input mt-1 resize-none" />
                </label>
            </div>

            {/* Submit */}
            <div className="sticky bottom-0 -mx-4 px-4 py-3 bg-white/95 backdrop-blur border-t border-slate-200 flex items-center justify-between gap-3">
                <div className="text-xs text-slate-500">
                    {remaining === 0
                        ? "All questions answered — ready to submit."
                        : `Answer the remaining ${remaining} question${remaining === 1 ? "" : "s"} to submit.`}
                </div>
                <button type="button" onClick={submit}
                        disabled={busy || remaining > 0}
                        data-testid="survey-submit"
                        className="px-6 py-3 rounded-full bg-emerald-600 text-white font-black text-sm uppercase tracking-wider hover:bg-emerald-700 transition disabled:opacity-40 disabled:cursor-not-allowed shadow-lg">
                    {busy ? "Submitting…" : "Submit anonymously"}
                </button>
            </div>
        </SurveyShell>
    );
}

function SurveyShell({ config, children }) {
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/40 to-slate-100" data-testid="engagement-survey-page">
            <header className="bg-[#081A36] text-white px-4 md:px-8 py-4">
                <div className="max-w-3xl mx-auto flex items-center justify-between">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-300">
                            Anonymous · Bi-monthly · {config.current_period_human}
                        </div>
                        <h1 className="text-xl md:text-2xl font-black">
                            Employee Engagement Survey
                        </h1>
                    </div>
                    <Link to="/login" data-testid="survey-back-to-login"
                          className="text-xs text-slate-300 hover:text-white">
                        ← Sign-in
                    </Link>
                </div>
            </header>
            <main className="max-w-3xl mx-auto px-4 md:px-8 py-6">
                <div className="mb-4 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-sm text-emerald-900 leading-relaxed">
                    <b>Your voice matters.</b> This survey is 100% anonymous — no names, no login,
                    no way to trace answers back to you. Please answer honestly. It takes about 5 minutes
                    and results feed directly into HR&apos;s people plan.
                </div>
                {children}
            </main>
        </div>
    );
}
